import { Set } from 'immutable';
import { Fragment, useState } from 'react';
import type { UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';

import { getAccountWorkChatSettings } from '@/data/account/selectors/accountSettings';
import { CONFIRMATION_DIALOG, openDialog } from '@/dialogs';
import { errorNotice } from '@/notices';
import { getAuthUser } from '@/shared/auth/selectors';
import FontIcon from '@/shared/ui/FontIcon';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { STREAM_CHANNEL_TYPES, WORKCHAT_VIEWS } from '@/workchat/constants';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { viewStackPop, viewStackPush, viewStackReset } from '@/workchat/v2/store/reducer';
import HeaderV3, { type HeaderButton } from '@/workchat/v3/components/HeaderV3/HeaderV3';
import ChannelNameInput from '@/workchat/v3/components/NewChannel/ChannelNameInput/ChannelNameInput';
import BigParticipantListV3 from '@/workchat/v3/components/ViewConversationV3/ConversationDetailsV3/BigParticipantListV3/BigParticipantListV3';

import '@/workchat/v3/components/ViewConversationV3/ConversationDetailsV3/ConversationDetailsV3.scss';

export default function ConversationDetailsV3() {
  const dispatch = useWiwDispatch();
  const authUser = useWiwSelector(getAuthUser);
  const accountWorkChatSettings = useWiwSelector(getAccountWorkChatSettings);

  const { client } = useStreamProvider();
  const { channel } = useChatContext();

  const [channelName, setChannelName] = useState<string>(channel?.data?.name || '');
  const [inputChannelName, setInputChannelName] = useState<string>(channel?.data?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [userIds, setUserIds] = useState<Set<string>>(Set());

  const canEdit = channel?.type === STREAM_CHANNEL_TYPES.CHAT && accountWorkChatSettings.customChannelsAllowed;
  const canLeave = channel?.type === STREAM_CHANNEL_TYPES.CHAT;
  const hasChanges = channelName !== inputChannelName.trim();

  const headerTitle = channel?.type === STREAM_CHANNEL_TYPES.CHAT ? 'Details' : 'Members';

  const back = () => {
    dispatch(viewStackPop());
  };

  const backButton: HeaderButton = {
    title: 'Back',
    action: back,
    label: [<FontIcon className="mr-1" key="back" icon="chevron-left" />, 'Back'],
    side: 'left',
    color: 'secondary',
  };

  const handleSave = async () => {
    if (!client) {
      return;
    }

    setIsSaving(true);

    try {
      const channels = await client?.queryChannels({ name: inputChannelName });

      if (channels && channels.length > 0) {
        setError('Channel name already exists.');
        return;
      }

      const updateResponse = await channel?.updatePartial({
        set: { name: inputChannelName },
      });

      if (updateResponse?.channel.name) {
        setChannelName(updateResponse?.channel.name);
      }
    } catch (_error) {
      dispatch(errorNotice('There was an issue updating the channel, try again later.', { target: 'page' }));
    } finally {
      setIsSaving(false);
    }
  };

  const buttons: HeaderButton[] = canEdit
    ? [
        backButton,
        {
          title: 'Save',
          action: handleSave,
          label: ['Save'],
          side: 'right',
          color: 'primary',
          disabled: isSaving || !hasChanges || !!error,
        },
      ]
    : [backButton];

  const handleAddClick = () => {
    dispatch(viewStackPush([WORKCHAT_VIEWS.VIEW_ADD_PARTICIPANTS, { channelUserIds: userIds }]));
  };

  const handleChannelNameChange = (newChannelName: string) => {
    if (error) {
      setError('');
    }

    if (!newChannelName) {
      setError('A channel name is required.');
    }

    setInputChannelName(newChannelName);
  };

  const handleUserListUpdate = (users: UserResponse[]) => {
    const newUserIdSet = users.reduce((userIdSet, user) => userIdSet.add(user.id), userIds);
    setUserIds(newUserIdSet);
  };

  const handleLeaveClick = () => {
    const leaveChannel = async () => {
      setIsSaving(true);
      try {
        await channel?.removeMembers([`${authUser.id}`]);
        dispatch(viewStackReset());
      } catch (_error) {
        // TODO Sentry error
        dispatch(errorNotice('There was an issue leaving the channel, try again later.', { target: 'page' }));
      } finally {
        setIsSaving(false);
      }
    };

    return dispatch(
      openDialog(CONFIRMATION_DIALOG, {
        title: 'Leave Conversation',
        body: <p>Are you sure you want to leave the conversation?</p>,
        confirmClass: 'btn-danger',
        confirmLabel: 'Leave',
        onConfirm: leaveChannel,
        width: 350,
      }),
    );
  };

  const renderParticipantListHeader = () => {
    if (!canEdit) {
      return;
    }

    return (
      <Fragment>
        <ChannelNameInput value={inputChannelName} autoFocus onChange={handleChannelNameChange} error={error} />
        <div className="participant-list-header">
          <h3>Channel Members</h3>
          <button type="button" onClick={handleAddClick} className="btn btn-primary add-button">
            Add
          </button>
        </div>
      </Fragment>
    );
  };

  const renderFooter = () => {
    if (canLeave) {
      return (
        <div className="footer">
          <button type="button" disabled={isSaving} onClick={handleLeaveClick} className="leave">
            Leave Channel
          </button>
        </div>
      );
    }
  };

  return (
    <div>
      <HeaderV3 borderBottom={!canEdit} buttons={buttons}>
        {headerTitle}
      </HeaderV3>
      <div className="ConversationDetailsV3">
        {renderParticipantListHeader()}
        <BigParticipantListV3 onUsersChange={handleUserListUpdate} />
        {renderFooter()}
      </div>
    </div>
  );
}
